import axios, { AxiosError } from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';

import { dispatchUnauthorizedEvent } from 'contexts/auth';
import { removeQueryParamsEmptyKeys } from 'util/general';
import { getItem, LOCALSTORAGE_KEYS, setItem } from 'util/localStorage';

// How long before a token's actual expiry we should treat it as expired, so we
// never send a request with a token that expires mid-flight.
const TOKEN_EXPIRY_BUFFER_MS = 60000;

type TokenRefresher = () => Promise<string>;

let tokenRefresher: TokenRefresher | null = null;

// Lets AuthProvider hand us a way to silently mint a fresh access token,
// without this module needing access to the useAuth0 hook directly.
export function setTokenRefresher(refresher: TokenRefresher | null) {
  tokenRefresher = refresher;
}

function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true;
  }

  try {
    const { exp } = jwtDecode<JwtPayload>(token);

    return !exp || exp * 1000 - TOKEN_EXPIRY_BUFFER_MS <= Date.now();
  } catch {
    return true;
  }
}

// Shared across concurrent requests so a burst of calls with an expired token
// triggers a single refresh instead of one per request.
let pendingRefresh: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (!tokenRefresher) {
    return null;
  }

  if (!pendingRefresh) {
    pendingRefresh = tokenRefresher().finally(() => {
      pendingRefresh = null;
    });
  }

  return pendingRefresh;
}

axios.interceptors.request.use(async (config) => {
  // If we have request parameters, we want to strip out any empty ones.
  if (config.params) {
    config.params = removeQueryParamsEmptyKeys(config.params);
  }

  if (isTokenExpired(getItem(LOCALSTORAGE_KEYS.TOKEN))) {
    const freshToken = await refreshAccessToken();

    if (freshToken) {
      setItem(LOCALSTORAGE_KEYS.TOKEN, freshToken);
      axios.defaults.headers.common.Authorization = `Bearer ${freshToken}`;
      config.headers.Authorization = `Bearer ${freshToken}`;
    }
  }

  // "randomUUID" is not supported in all older browsers.
  // See https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID#browser_compatibility
  config.headers['Request-Id'] = window.crypto.randomUUID?.();

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error instanceof AxiosError) {
      // If we ever get back an Unauthorized API response code, we should log the user out.
      if (error.response?.status === 401) {
        dispatchUnauthorizedEvent();
      }
    }

    return Promise.reject(error);
  },
);
